/**
 * MobilePhaseInfoPopover — paper-card popover that explains the current
 * turn phase to a mobile player.
 *
 * Why this exists: desktop's StatusBar has a small `?` button next to the
 * phase chip that reveals a tooltip with the canonical phase explanation.
 * Mobile players had no equivalent surface — PhaseCoach offers a one-line
 * action hint, but not the underlying "what is this phase actually for?"
 * answer. This popover restores parity using the same source-of-truth
 * data (PHASE_LABELS + PHASE_EXPLANATIONS + PHASE_TINTS exported from
 * PhaseTransitionToast) so the educational copy never drifts.
 *
 * Visuals:
 *   - cream paper card (matches the parchment surface system)
 *   - 3px phase-tinted left border (same colour family used by the
 *     PhaseChip in StatusBar and the PhaseTransitionToast)
 *   - Fraunces title at 16px / weight 600, Newsreader body at 14px
 *   - subtle pencil-graphite border + a slight -0.5deg rotation to keep
 *     the chalk/sketch feel
 *   - width clamped to min(85vw, 320px) so it fits a 320px-wide phone
 *     with comfortable padding while never running off the right edge
 *
 * Anchoring:
 *   The caller passes the bounding rect of the `?` button (read via
 *   getBoundingClientRect at toggle time). The popover positions itself
 *   below the button's bottom edge, horizontally clamped so it never
 *   overflows either viewport edge with a 12px margin.
 *
 * Dismiss surfaces:
 *   - tap the transparent backdrop
 *   - tap the `?` again (caller handles this)
 *   - press Escape
 *
 * Z-index: 25 (above the dashboard sheet at 20, below the tutorial
 * overlay at 30).
 *
 * Honours `prefers-reduced-motion`: skips the slide-in, fades only.
 */

import { useEffect, type ReactNode } from 'react';
import { motion } from 'motion/react';
import type { TurnPhase } from '@qd/protocol';
import { fontStacks, surfaces } from '@qd/design-tokens';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import {
  PHASE_EXPLANATIONS,
  PHASE_LABELS,
  PHASE_TINTS,
} from '../PhaseTransitionToast';

interface MobilePhaseInfoPopoverProps {
  readonly phase: TurnPhase;
  /**
   * Bounding rect of the trigger button (the `?` icon). The popover
   * positions its top edge just below `anchorRect.bottom` and centres
   * itself horizontally under the button, clamped to viewport edges.
   */
  readonly anchorRect: DOMRect;
  readonly onClose: () => void;
}

const POPOVER_WIDTH_CSS = 'min(85vw, 320px)';
const VIEWPORT_MARGIN = 12;
const ANCHOR_GAP = 8;

/**
 * Resolve a horizontal `left` value (in px) that centres the popover
 * under the trigger and keeps it inside the viewport with a 12px margin.
 *
 * We compute against `window.innerWidth` because the popover is fixed-
 * positioned. The CSS `width` clamp is the same expression we use for
 * style, evaluated against innerWidth for the clamp branch.
 */
function resolveLeft(anchorCenterX: number, viewportWidth: number): number {
  const popoverWidth = Math.min(0.85 * viewportWidth, 320);
  const half = popoverWidth / 2;
  const min = VIEWPORT_MARGIN;
  const max = viewportWidth - popoverWidth - VIEWPORT_MARGIN;
  const desired = anchorCenterX - half;
  if (max < min) return min; // viewport narrower than popover + margins
  return Math.max(min, Math.min(max, desired));
}

export function MobilePhaseInfoPopover({
  phase,
  anchorRect,
  onClose,
}: MobilePhaseInfoPopoverProps): ReactNode {
  const reduced = useReducedMotion();
  const title = PHASE_LABELS[phase];
  const body = PHASE_EXPLANATIONS[phase];
  const tint = PHASE_TINTS[phase];

  // Escape-to-dismiss. Mounted only while the popover is rendered
  // because the parent renders this component conditionally.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : 360;
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const left = resolveLeft(anchorCenterX, viewportWidth);
  const top = anchorRect.bottom + ANCHOR_GAP;

  return (
    <>
      {/*
        Transparent backdrop — fills the viewport so any tap outside the
        card dismisses the popover. zIndex sits one below the card so
        clicks on the card itself don't bubble through.
      */}
      <div
        aria-hidden
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'transparent',
          zIndex: 24,
        }}
      />
      <motion.div
        role="dialog"
        aria-label={`Phase information: ${title}`}
        initial={
          reduced
            ? { opacity: 0 }
            : { opacity: 0, y: -6, scale: 0.98 }
        }
        animate={
          reduced
            ? { opacity: 1 }
            : { opacity: 1, y: 0, scale: 1 }
        }
        exit={
          reduced
            ? { opacity: 0 }
            : { opacity: 0, y: -4, scale: 0.98 }
        }
        transition={
          reduced
            ? { duration: 0.14 }
            : {
                duration: 0.22,
                ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
              }
        }
        style={{
          position: 'fixed',
          top,
          left,
          width: POPOVER_WIDTH_CSS,
          maxWidth: POPOVER_WIDTH_CSS,
          zIndex: 25,
          padding: '12px 14px 13px',
          background: surfaces.paper,
          borderLeft: `3px solid ${tint}`,
          borderTop: `1px solid #3A3833`,
          borderRight: `1px solid #3A3833`,
          borderBottom: `1px solid #3A3833`,
          borderRadius: 4,
          boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
          transform: 'rotate(-0.5deg)',
          transformOrigin: 'top left',
          color: surfaces.ink,
        }}
      >
        <div
          style={{
            fontFamily: fontStacks.display,
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.2,
            color: surfaces.ink,
            marginBottom: 6,
            letterSpacing: 0,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: fontStacks.body,
            fontSize: 14,
            lineHeight: 1.45,
            color: surfaces.ink,
            opacity: 0.86,
            letterSpacing: 0,
          }}
        >
          {body}
        </div>
        {/*
          "View full rules" footer link — restores mobile access to the full
          HowToPlayModal that was previously reachable via the TitleCartouche
          tap on the map (the cartouche has since been removed for visual
          clarity on the new Risk SVG board). Dispatches the same
          `qd:show-howto` event the modal listens for in Play.tsx.
        */}
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('qd:show-howto'));
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            marginTop: 10,
            padding: '8px 0 0',
            background: 'transparent',
            border: 'none',
            borderTop: `1px dashed rgba(58,56,51,0.32)`,
            fontFamily: fontStacks.mono,
            fontSize: 11,
            letterSpacing: '0.12em',
            color: surfaces.ink,
            opacity: 0.72,
            cursor: 'pointer',
            textAlign: 'left',
            textTransform: 'uppercase',
          }}
        >
          View full rules →
        </button>
      </motion.div>
    </>
  );
}
